/**
 * Mermaid Manager - Global state management for Mermaid initialization
 * Prevents multiple initialization conflicts and provides centralized error handling.
 *
 * mermaid.render() is **not** safe for concurrent calls – it creates a
 * temporary DOM element and reads back its `firstChild`.  When two calls
 * overlap, the second one removes the first's element, causing
 * "Cannot read properties of null (reading 'firstChild')".
 *
 * This module therefore serialises every render through a promise-based
 * queue so that at most one mermaid.render() is in-flight at any time.
 */

import mermaid from 'mermaid';
import type { MermaidConfig } from 'mermaid';

import { createMermaidConfig } from './mermaidConfig';

// Global state to track Mermaid initialization
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
let currentConfig: MermaidConfig | null = null;

/**
 * Initialize Mermaid globally with the provided configuration
 * Uses singleton pattern to prevent multiple initializations
 */
export const initializeMermaidGlobally = async (isDark = false): Promise<void> => {
  const config = createMermaidConfig(isDark);
  
  // If already initialized with the same config, skip
  if (isInitialized && currentConfig && JSON.stringify(currentConfig) === JSON.stringify(config)) {
    return;
  }
  
  // If initialization is in progress, wait for it
  if (initializationPromise) {
    await initializationPromise;
    return;
  }
  
  // Start new initialization
  initializationPromise = performInitialization(config);
  
  try {
    await initializationPromise;
    isInitialized = true;
    currentConfig = config;
  } catch (error) {
    isInitialized = false;
    currentConfig = null;
    throw error;
  } finally {
    initializationPromise = null;
  }
};

/**
 * Perform the actual Mermaid initialization
 */
const performInitialization = async (config: MermaidConfig): Promise<void> => {
  // Clean up any previous state
  if (typeof mermaid.mermaidAPI?.reset === 'function') {
    mermaid.mermaidAPI.reset();
  }
  
  // Initialize with new config
  mermaid.initialize(config);
  
  // Add a small delay to ensure initialization completes
  await new Promise(resolve => setTimeout(resolve, 100));
  
};

/**
 * Reset Mermaid initialization state
 * Useful for testing or when switching between different configurations
 */
export const resetMermaidState = (): void => {
  isInitialized = false;
  initializationPromise = null;
  currentConfig = null;
  
  try {
    if (typeof mermaid.mermaidAPI?.reset === 'function') {
      mermaid.mermaidAPI.reset();
    }
  } catch (error) {
    // Silently ignore reset errors as they don't affect functionality
    void error;
  }
};

/**
 * Check if Mermaid is currently initialized
 */
export const isMermaidInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Get the current Mermaid configuration
 */
export const getCurrentMermaidConfig = (): MermaidConfig | null => {
  return currentConfig;
};

/**
 * Remove orphaned Mermaid error elements that mermaid.render() leaves behind
 * in document.body when it fails (elements with IDs like "d{diagramId}").
 * Only removes the specific element for the given ID — a broad sweep would
 * destroy temporary elements belonging to in-flight concurrent renders.
 */
export const cleanupMermaidErrorElements = (diagramId?: string): void => {
  if (diagramId) {
    // Mermaid can leave more than one orphan with the same id="d{id}"
    // (e.g. the primary render and the fallback both append one on
    // failure).  getElementById only returns the first match, so loop
    // until every duplicate has been removed.
    let specific = document.getElementById(`d${diagramId}`);
    while (specific) {
      specific.remove();
      specific = document.getElementById(`d${diagramId}`);
    }
  }
};

// Simple mutex so that mermaid.render() calls are serialized.
// Mermaid uses global internal state that is not safe for
// concurrent rendering — a second call while the first is
// in-flight corrupts the shared DOM and causes
// "element.firstChild is null" crashes.
let renderQueue: Promise<void> = Promise.resolve();

/**
 * Render a Mermaid diagram with proper error handling.
 * Calls are serialized via a queue to avoid concurrent
 * mermaid.render() invocations.
 */
export const renderMermaidDiagram = async (
  id: string,
  code: string,
  isDark = false,
): Promise<{ svg: string }> => {
  // Chain onto the queue so only one render runs at a time.
  let resolve: () => void;
  const next = new Promise<void>((r) => { resolve = r; });
  const previous = renderQueue;
  renderQueue = next;

  // Wait for any prior render to finish.
  await previous;

  try {
    // Re-initialize mermaid before every render.  Mermaid's
    // render() can mutate its own internal theme state (e.g.
    // via %%{init}%% directives or classDef overrides in the
    // diagram source), so we must reset it to the Shell
    // branded config each time to prevent colour bleed
    // between diagrams.
    const config = createMermaidConfig(isDark);
    mermaid.initialize(config);

    // Clean up any leftover error element from a previous
    // failed render with this specific ID.
    cleanupMermaidErrorElements(id);

    try {
      // Use the modern API
      const result = await mermaid.render(id, code);
      return result;
    } catch (primaryError) {
      // mermaid.render() may have left a temporary element
      cleanupMermaidErrorElements(id);

      try {
        // Fallback to older API if available
        const fallbackResult = await (
          mermaid as unknown as {
            mermaidAPI: {
              render: (
                _id: string,
                _code: string,
              ) => Promise<{ svg: string }>;
            };
          }
        ).mermaidAPI.render(id, code);
        return {
          svg:
            typeof fallbackResult === 'string'
              ? fallbackResult
              : fallbackResult.svg || '',
        };
      } catch {
        throw primaryError;
      }
    }
  } finally {
    // mermaid.render() appends a temporary measuring element (#d{id}) to
    // document.body and is supposed to remove it - but it often does not,
    // leaving the full-size diagram lingering at the very end of <body>. That
    // invisible element inflates the document height and shows up as a large
    // blank "endless scroll" below the page content. Sweep it after every
    // render (success or failure). Renders are serialized through this queue,
    // so removing the current id's element never races a concurrent render.
    cleanupMermaidErrorElements(id);
    // Release the lock so the next queued render can start.
    resolve!();
  }
};
